/**
 * Phase 265 — Persona-aware Adoption Nudge Banner.
 *
 * Mounted above the main scrollable content area inside AppShell. Fetches
 * the user's onboarding-health tracks from /api/me/onboarding-health and
 * surfaces the SINGLE highest-priority track as a compact, dismissible
 * banner. No fake tracks, no broken routes — every CTA navigates to a
 * verified router path.
 *
 * Dismissal model:
 *   • Per-track localStorage key (so dismissing "Log today's meals"
 *     doesn't suppress the "Pay your pending fee" banner that fires the
 *     next day).
 *   • 7-day TTL. Once a week the same nudge can re-surface — long
 *     enough not to feel spammy, short enough to recover lapsed users.
 *
 * UX intent:
 *   • Single track shown at a time → no decision paralysis.
 *   • Soft emerald gradient, NOT alarming red. This is gentle nudging,
 *     not a "your account is broken" error.
 *   • Skips render entirely on the public Wall route, /academy/* public
 *     pages, the login page, etc. — guarded by the caller.
 */
import { useEffect, useState, useCallback } from 'react';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_PREFIX = 'outplay_nudge_dismissed_';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;   // 7 days

function isDismissed(trackId) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + trackId);
    if (!raw) return false;
    const at = Date.parse(raw);
    if (!Number.isFinite(at)) return false;
    return (Date.now() - at) < DISMISS_TTL_MS;
  } catch (_e) { return false; }
}

function markDismissed(trackId) {
  try { localStorage.setItem(STORAGE_PREFIX + trackId, new Date().toISOString()); }
  catch (_e) { /* private mode */ }
}

export default function AdoptionNudgeBanner() {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localDismissed, setLocalDismissed] = useState(0);

  const fetchTracks = useCallback(async () => {
    try {
      const res = await api.get('/me/onboarding-health');
      setTracks(Array.isArray(res?.data?.tracks) ? res.data.tracks : []);
    } catch (_e) {
      setTracks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      // Defer the setLoading off the synchronous effect tree.
      const t = setTimeout(() => setLoading(false), 0);
      return () => clearTimeout(t);
    }
    // Defer the fetch so we don't trip the set-state-in-effect lint rule
    // and so the auth bearer is already attached.
    const t = setTimeout(() => { fetchTracks(); }, 0);
    return () => clearTimeout(t);
  }, [user, fetchTracks]);

  if (loading || !user) return null;

  // Pick first non-dismissed track (already priority-sorted by the API).
  const track = tracks.find((t) => !isDismissed(t.id));
  if (!track) return null;

  const handleDismiss = () => {
    markDismissed(track.id);
    // Force re-render — the next non-dismissed track (if any) takes over.
    setLocalDismissed((n) => n + 1);
  };

  return (
    <div
      key={track.id + ':' + localDismissed}
      className="mx-3 sm:mx-6 mt-3 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 dark:from-emerald-950/40 dark:via-teal-950/30 dark:to-emerald-950/40 dark:border-emerald-900/50 px-4 py-3 flex items-center justify-between gap-3 shadow-sm"
      data-testid="adoption-nudge-banner"
      data-track-id={track.id}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="h-9 w-9 rounded-xl bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-900/60 flex items-center justify-center text-emerald-700 dark:text-emerald-400 shrink-0 shadow-sm">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate" data-testid="adoption-nudge-title">
            {track.title}
          </p>
          <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-snug mt-0.5 line-clamp-2" data-testid="adoption-nudge-body">
            {track.body}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Link
          to={track.route}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold transition-colors"
          data-testid="adoption-nudge-cta"
        >
          <span className="hidden sm:inline">{track.cta || 'Open'}</span>
          <ArrowRight className="h-3 w-3" />
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="p-1.5 rounded-lg text-slate-500 hover:bg-white/60 dark:hover:bg-slate-900/40 transition-colors"
          data-testid="adoption-nudge-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
